import { useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { Monitor, Smartphone, Tablet } from "lucide-react";
import {
  type BlockSpacingBox,
  type BlockStyle,
  type SpacingSide,
  type StyleDevice,
} from "@/lib/cms-puck/block-style";
import { SECTION_CARD_CLASS } from "./constants";
import { IconToggle } from "./controls";

/* ------------------------ Bascule Bureau/Tablette/Mobile ------------------ */

export const DEVICES: Array<
  [StyleDevice, CmsNewKey, string, React.ReactNode]
> = [
  ["desktop", "cms.new.fields.style.deviceDesktop", "Bureau", <Monitor className="h-4 w-4" />],
  ["tablet", "cms.new.fields.style.deviceTablet", "Tablette", <Tablet className="h-4 w-4" />],
  ["mobile", "cms.new.fields.style.deviceMobile", "Mobile", <Smartphone className="h-4 w-4" />],
];

/**
 * Bascule d'appareil façon Elementor. Tablette/mobile n'éditent QUE les valeurs
 * dimensionnelles (marges, largeur, taille de texte…) en SURCHARGE : une valeur
 * vide hérite du niveau supérieur (bureau → tablette → mobile).
 */
export function DeviceSwitcher({
  device,
  onChange,
}: {
  device: StyleDevice;
  onChange: (device: StyleDevice) => void;
}) {
  const cmsNew = useCmsNew();
  return (
    <div className={SECTION_CARD_CLASS}>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-0.5">
          <span className="text-sm font-semibold text-slate-900">{cmsNew("cms.new.auto.text.appareil", "Appareil")}</span>
          <p className="break-words text-xs leading-relaxed text-slate-500">
            {cmsNew(
              "cms.new.fields.style.deviceInheritHint",
              "Tablette et mobile héritent des valeurs du niveau supérieur.",
            )}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {DEVICES.map(([value, key, fallback, icon]) => (
            <IconToggle
              key={value}
              label={cmsNew(key, fallback)}
              active={device === value}
              onClick={() => onChange(value)}
            >
              {icon}
            </IconToggle>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------- Héritage desktop → tablette → mobile --------------- */

/** Valeur desktop d'un scalaire responsive (largeur / taille / lettrage / interligne). */
function desktopScalar(
  style: BlockStyle,
  key: "width" | "fontSize" | "letterSpacing" | "lineHeight",
): SpacingSide {
  if (key === "width") return style.layout.width;
  return style.design.text[key];
}

/** Valeur HÉRITÉE (placeholder) d'un scalaire pour un appareil donné. */
export function inheritedScalar(
  style: BlockStyle,
  device: "tablet" | "mobile",
  key: "width" | "fontSize" | "letterSpacing" | "lineHeight",
): SpacingSide {
  if (device === "mobile" && style.responsive.tablet[key] != null) {
    return style.responsive.tablet[key];
  }
  return desktopScalar(style, key);
}

/** Boîte margin/padding HÉRITÉE (placeholders) pour un appareil donné. */
export function inheritedBox(
  style: BlockStyle,
  device: "tablet" | "mobile",
  group: "margin" | "padding",
): BlockSpacingBox {
  const desktop = style.spacing[group];
  if (device === "tablet") return desktop;
  const tablet = style.responsive.tablet[group];
  return {
    top: tablet.top ?? desktop.top,
    right: tablet.right ?? desktop.right,
    bottom: tablet.bottom ?? desktop.bottom,
    left: tablet.left ?? desktop.left,
  };
}
